import { useQueryClient } from "@tanstack/solid-query";
import { createFileRoute } from "@tanstack/solid-router";
import { createSignal, Show } from "solid-js";
import { toast } from "solid-sonner";
import BankConnectionList from "~/components/bank-connection/bank-connection-list";
import { BankUpgradeDialog } from "~/components/bank-connection/bank-upgrade-dialog";
import {
  IconEllipsisVertical,
  IconLink,
  IconLoaderCircle,
} from "~/components/icons";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { getBankConnectionsPageData } from "~/features/banking/banking-connections-page";
import {
  createLinkToken,
  exchangePublicToken,
} from "~/features/banking/banking.api";
import {
  BankingConnectionsResource,
  bankingQueryKeys,
} from "~/features/banking/banking.resource";
import { openPlaidLink } from "~/features/banking/plaid-link";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import { withBusinessGuard } from "~/lib/route-guards";
import {
  extractErrorMessage,
  resolveServerErrorBody,
} from "~/lib/server-fn-error";
import type { PlaidLinkOnSuccessMetadata } from "~/shims/plaid-link";

export const Route = createFileRoute("/_dashboard/dashboard/bank-connections/")(
  {
    ...withBusinessGuard(),
    loader: async ({ context }) => {
      const collection = await context.queryClient.ensureQueryData(
        businessesResource.options(undefined),
      );
      const businessId = collection.activeBusinessId!;
      const data = await getBankConnectionsPageData({ data: { businessId } });
      BankingConnectionsResource.seed(
        context.queryClient,
        { businessId },
        data.bankingOverview,
      );
      businessDetailResource.seed(
        context.queryClient,
        businessId,
        data.businessDetail,
      );
      return {
        businessId,
        bankingOverview: data.bankingOverview,
        activeBusiness: data.businessDetail,
      };
    },
    component: BankConnectionsPage,
  },
);

function BankConnectionsPage() {
  const qc = useQueryClient();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );
  BankingConnectionsResource.seedIfEmpty(
    qc,
    { businessId: data().businessId },
    data().bankingOverview,
  );

  const businessId = useActiveBusinessId();

  const [connecting, setConnecting] = createSignal(false);
  const [upgradeDialog, setUpgradeDialog] = createSignal<{
    open: boolean;
    message: string;
  }>({ open: false, message: "" });

  async function invalidateAfterConnect() {
    // Await the invalidations so the UI deterministically refreshes with the
    // new connection instead of relying on a fire-and-forget background
    // refetch (which, combined with keepPreviousData, can leave the old list
    // on screen until the refetch happens to land).
    await Promise.all([
      qc.invalidateQueries({
        queryKey: bankingQueryKeys.connections(businessId() ?? ""),
        refetchType: "active",
      }),
      qc.invalidateQueries({
        queryKey: bankingQueryKeys.overview(businessId() ?? ""),
        refetchType: "active",
      }),
      qc.invalidateQueries({
        queryKey: ["bank-account", businessId() ?? ""],
        refetchType: "active",
      }),
      // Connecting enqueues an async sync that inserts staged transactions in
      // the background. Invalidate the transactions list + category facets so
      // a later visit to the bank-transactions route refetches fresh data
      // instead of showing a stale (often empty) cached page.
      qc.invalidateQueries({
        queryKey: ["bank-transaction", businessId() ?? ""],
        refetchType: "active",
      }),
      qc.invalidateQueries({
        queryKey: ["bank-transaction-categories", businessId() ?? ""],
        refetchType: "active",
      }),
    ]);
  }

  async function handlePlaidSuccess(
    publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) {
    try {
      const result = await exchangePublicToken({
        data: {
          businessId: businessId() ?? "",
          publicToken,
          institution: metadata.institution
            ? {
                id: metadata.institution.id ?? undefined,
                name: metadata.institution.name ?? undefined,
              }
            : undefined,
        },
      });
      // A 4xx from the server fn resolves as a raw Response (or, in some
      // paths, the parsed error body) — normalize it and surface the message.
      const errBody = await resolveServerErrorBody(result);
      if (errBody) {
        if (errBody.code === "BANK_CONNECTION_LIMIT") {
          setUpgradeDialog({ open: true, message: errBody.error ?? "" });
        } else {
          toast.error(errBody.error ?? "Failed to connect your bank.");
        }
        return;
      }
      toast.success("Bank connected — transactions will sync shortly.");
      await invalidateAfterConnect();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to connect your bank."));
    }
  }

  async function handleConnectBank() {
    setConnecting(true);
    try {
      const result = await createLinkToken({
        data: { businessId: businessId() ?? "" },
      });
      // Free-tier / rate-limit errors resolve as a raw Response (or, in some
      // paths, the parsed error body) — normalize before opening Plaid Link.
      const errBody = await resolveServerErrorBody(result);
      if (errBody) {
        if (errBody.code === "BANK_CONNECTION_LIMIT") {
          setUpgradeDialog({ open: true, message: errBody.error ?? "" });
        } else {
          toast.error(errBody.error ?? "Failed to start Plaid Link.");
        }
        return;
      }
      await openPlaidLink({
        token: result.linkToken,
        onSuccess: handlePlaidSuccess,
        onExit: (_err, exitMeta) => {
          if (exitMeta?.status && exitMeta.status !== "requires_credentials") {
            toast("Bank connection cancelled");
          }
        },
      });
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to start Plaid Link."));
    } finally {
      setConnecting(false);
    }
  }

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 pb-3 pt-6 sm:px-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold">Bank Connections</h1>
          <p class="text-muted-foreground">
            Manage the banks linked to this business.
          </p>
        </div>
        <DropdownMenu placement="bottom-end">
          <DropdownMenuTrigger
            as={Button<"button">}
            variant="outline"
            size="icon"
          >
            <IconEllipsisVertical class="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent class="w-52">
            <DropdownMenuItem
              onClick={handleConnectBank}
              disabled={connecting()}
            >
              <Show when={connecting()} fallback={<IconLink class="size-4" />}>
                <IconLoaderCircle class="size-4 animate-spin" />
              </Show>
              {connecting() ? "Opening…" : "Connect Bank (Plaid)"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <BankConnectionList businessId={businessId() ?? ""} />
      <BankUpgradeDialog
        open={upgradeDialog().open}
        message={upgradeDialog().message}
        onOpenChange={(open) => setUpgradeDialog((s) => ({ ...s, open }))}
      />
    </div>
  );
}
